import wordpressData from './generated-wordpress.json'
import supplementalProducts from './supplemental-products.json'

type WordpressEntry = {
  id: number
  title: string
  slug: string
  status: string
  excerpt: string
  html: string
  publishedAt: string
  author: string
  categories?: string[]
  tags?: string[]
  price?: number | null
  regularPrice?: number | null
  salePrice?: number | null
  image?: string | null
  attributes?: Record<string, string>
}

type WordpressDataset = {
  posts: WordpressEntry[]
  products: WordpressEntry[]
}

export type Product = {
  id: number
  title: string
  slug: string
  summary: string
  descriptionHtml: string
  price: number | null
  regularPrice: number | null
  salePrice: number | null
  categories: string[]
  tags: string[]
  image: string | null
  attributes: Record<string, string>
  taxonomy: {
    catalogGroup: string
    productFamily: string
    deploymentContexts: string[]
    hardwareTypes: string[]
    capabilities: string[]
  }
}

export type BlogPost = {
  id: number
  title: string
  slug: string
  excerpt: string
  html: string
  publishedAt: string
  author: string
  categories: string[]
  tags: string[]
}

export type ContentPage = {
  title: string
  summary: string
  html: string
}

export type FaqItem = {
  id: string
  question: string
  answer: string
}

export type CmsData = {
  settings: {
    brandName: string
    tagline: string
    primaryEmail: string
    footerNote: string
    logoPath: string
    logoLightPath: string
  }
  homepage: {
    eyebrow: string
    title: string
    summary: string
    processTitle: string
    ctaTitle: string
    ctaBody: string
    heroMediaPath: string
    metrics: Array<{ label: string; value: string }>
    industries: Array<{ name: string; description: string; imagePath: string }>
    steps: Array<{ title: string; body: string }>
  }
  pages: {
    about: ContentPage
    contact: ContentPage
    faq: ContentPage
  }
  faqs: FaqItem[]
  posts: BlogPost[]
  products: Product[]
}

type SupplementalProductEntry = Omit<Product, 'attributes'> & {
  attributes: Record<string, string | undefined>
}

const parsedWordpress = wordpressData as WordpressDataset
const curatedSupplementalProducts = (supplementalProducts as SupplementalProductEntry[]).map(
  (product) => ({
    ...product,
    attributes: Object.fromEntries(
      Object.entries(product.attributes).filter((entry): entry is [string, string] =>
        typeof entry[1] === 'string',
      ),
    ),
  }),
)
const totalCatalogProducts = parsedWordpress.products.length + curatedSupplementalProducts.length

function unique(values: Array<string | null | undefined>) {
  return [...new Set(values.map((value) => value?.trim()).filter(Boolean))] as string[]
}

function includesAny(value: string, needles: string[]) {
  return needles.some((needle) => value.includes(needle))
}

function cleanImportedCopy(value: string) {
  return value
    .replace(/<strong>description<\/strong>/gi, '')
    .replace(/Digital Display Screen can vary depending on the current market\.[\s\S]*?(phone\.?|phone.”|phone")/gi, '')
    .replace(/For a more accurate cost, please contact support and place an order on the phone\.?[”"]?/gi, '')
    .replace(/The cost of the kit varies depending on the current market\./gi, '')
    .replace(/If a specific brand can be requested\./gi, 'Specific brands can be requested.')
    .replace(/If other size is needed, please include it in “Checkout Notes”\./gi, 'Additional sizes are available on request.')
    .replace(/\*\*/g, '')
    .replace(/[”]/g, '"')
    .trim()
}

function cleanImportedText(value: string) {
  return cleanImportedCopy(value)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizeImportedTitle(product: WordpressEntry) {
  const slug = product.slug
  const titleMap: Record<string, string> = {
    'menu-board': 'Digital Menu Board Kit',
    'tenant-directory': 'Tenant Directory Display Kit',
    'e-gates': 'E-Gate Display System',
    'visitor-notification': 'Visitor Check-In Display Kit',
    'employee-communications': 'Employee Communications Display Kit',
    'bulletin-board': 'Interactive Kiosk System',
    'foot-pedal': 'ADA Foot Pedal Controller',
    'hdmi-cable': 'Commercial HDMI Cable',
    'sandisk-sd-card': 'Industrial SD Card',
    'new-kit': 'Outdoor LED Display Bundle',
  }

  if (slug && titleMap[slug]) {
    return titleMap[slug]
  }

  if (product.title === 'Bulletin Board') {
    return 'Employee Communications Display Kit'
  }

  return product.title
}

function shouldIncludeImportedProduct(product: WordpressEntry) {
  const normalizedTitle = product.title.toLowerCase()

  if (product.status !== 'publish') {
    return false
  }

  if (!product.slug) {
    return false
  }

  if (includesAny(normalizedTitle, ['test product', 'sample product'])) {
    return false
  }

  return true
}

function buildImportedSummary(product: WordpressEntry, normalizedTitle: string) {
  const excerptText = cleanImportedText(product.excerpt)
  const htmlText = cleanImportedText(product.html)
  const source = excerptText || htmlText || normalizedTitle
  const concise = source.length > 210 ? `${source.slice(0, 207).trim()}...` : source

  return concise || normalizedTitle
}

function normalizeProduct(product: WordpressEntry): Product {
  const normalizedTitle = normalizeImportedTitle(product)
  const cleanedHtml = cleanImportedCopy(product.html)
  const sourceText = `${normalizedTitle} ${product.slug} ${cleanImportedText(product.excerpt)} ${cleanImportedText(product.html)}`.toLowerCase()
  const sourceCategories = product.categories ?? []
  const deploymentContexts = unique(
    sourceCategories.filter((category) =>
      ['Airport', 'Business', 'Entertainment', 'Gaming', 'Healthcare'].includes(category),
    ),
  )

  let catalogGroup = 'Accessories'
  let productFamily = 'Signage Accessories'
  let hardwareTypes: string[] = ['Accessory']
  let capabilities: string[] = []

  if (sourceCategories.includes('Kits') || includesAny(sourceText, ['kit', 'menu board', 'directory', 'visitor', 'bulletin board'])) {
    catalogGroup = 'Kiosks'
    productFamily = includesAny(sourceText, ['kiosk', 'interactive'])
      ? 'Freestanding Kiosks'
      : 'Deployment Kits'
    hardwareTypes = includesAny(sourceText, ['touch', 'interactive', 'kiosk'])
      ? ['Kiosk', 'Display']
      : ['Kit', 'Display']
    capabilities = unique([
      includesAny(sourceText, ['touch', 'interactive']) ? 'Touch-Ready' : null,
      includesAny(sourceText, ['directory']) ? 'Directories' : null,
      includesAny(sourceText, ['menu']) ? 'Menu Boards' : null,
      includesAny(sourceText, ['visitor']) ? 'Visitor Check-In' : null,
      includesAny(sourceText, ['bulletin', 'communications']) ? 'Employee Messaging' : null,
    ])
  }

  if (includesAny(sourceText, ['display', 'monitor', 'screen', 'led'])) {
    catalogGroup = includesAny(sourceText, ['outdoor', 'roadway']) ? 'Outdoor Solutions' : 'Displays'
    productFamily = includesAny(sourceText, ['outdoor', 'roadway'])
      ? 'Outdoor Displays'
      : includesAny(sourceText, ['touch', 'interactive'])
        ? 'Interactive Displays'
        : 'Commercial Displays'
    hardwareTypes = ['Display']
    capabilities = unique([
      ...capabilities,
      includesAny(sourceText, ['touch', 'interactive']) ? 'Touch-Ready' : null,
      includesAny(sourceText, ['4k', '3840']) ? '4K' : null,
      includesAny(sourceText, ['outdoor', 'roadway']) ? 'Outdoor Planning' : null,
    ])
  }

  if (includesAny(sourceText, ['brightsign', 'media player', 'android pc', 'android player'])) {
    catalogGroup = 'Media Players'
    productFamily = includesAny(sourceText, ['android']) ? 'Android Players' : 'BrightSign Players'
    hardwareTypes = ['Media Player']
    capabilities = unique([
      'Scheduled Playback',
      includesAny(sourceText, ['html5']) ? 'HTML5 Playback' : null,
      includesAny(sourceText, ['4k']) ? '4K Playback' : null,
    ])
  }

  if (includesAny(sourceText, ['mount', 'wall mount', 'ceiling'])) {
    catalogGroup = 'Mounts'
    productFamily = includesAny(sourceText, ['ceiling']) ? 'Ceiling Mounts' : 'Wall Mounts'
    hardwareTypes = ['Mount']
    capabilities = unique([
      includesAny(sourceText, ['ceiling']) ? 'Overhead Visibility' : 'Low Profile',
    ])
  }

  if (includesAny(sourceText, ['meeting room', 'room panel', 'scheduler'])) {
    catalogGroup = 'Room Panels'
    productFamily = 'Scheduling Panels'
    hardwareTypes = ['Panel']
    capabilities = unique(['Scheduling', 'Availability Display'])
  }

  if (sourceCategories.includes('Hardware') || includesAny(sourceText, ['pedal', 'cable', 'sd card'])) {
    catalogGroup = 'Accessories'
    productFamily = includesAny(sourceText, ['pedal'])
      ? 'Foot Pedals'
      : includesAny(sourceText, ['cable'])
        ? 'Cables'
        : includesAny(sourceText, ['sd card'])
          ? 'Storage Media'
          : productFamily
    hardwareTypes = ['Accessory']
    capabilities = unique([
      includesAny(sourceText, ['pedal']) ? 'Touch-Free Control' : null,
      includesAny(sourceText, ['cable']) ? 'Signal Delivery' : null,
      includesAny(sourceText, ['sd card']) ? 'Local Storage' : null,
    ])
  }

  const normalizedTaxonomy = {
    catalogGroup,
    productFamily,
    deploymentContexts: deploymentContexts.length ? deploymentContexts : ['Business'],
    hardwareTypes,
    capabilities,
  }

  return {
    id: product.id,
    title: normalizedTitle,
    slug: product.slug,
    summary: buildImportedSummary(product, normalizedTitle),
    descriptionHtml: cleanedHtml,
    price: product.price ?? null,
    regularPrice: product.regularPrice ?? null,
    salePrice: product.salePrice ?? null,
    categories: unique([
      normalizedTaxonomy.catalogGroup,
      normalizedTaxonomy.productFamily,
      ...normalizedTaxonomy.deploymentContexts,
      ...normalizedTaxonomy.hardwareTypes,
    ]),
    tags: product.tags ?? [],
    image: product.image ?? null,
    attributes: product.attributes ?? {},
    taxonomy: normalizedTaxonomy,
  }
}

function plainText(html: string) {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim()
}

function summarize(html: string, fallback: string) {
  const text = plainText(html)
  const source = text || fallback

  return source.length > 180 ? `${source.slice(0, 177).trim()}...` : source
}

const aboutHtml = `
  <p>We’re your go-to e-commerce destination for top-tier digital signage kits that are competitively priced and designed for easy self-installation upon delivery.</p>
  <p>Our mission is to help small to medium-sized businesses adopt modern digital signage and workplace technology without the cost and friction of traditional AV procurement.</p>
  <p>Think of the brand as the IKEA of digital signage: practical kits, clear documentation, and fast go-live without a heavyweight integration cycle.</p>
`

const contactHtml = `
  <p>Please read the FAQ before sending a message. If you already know your use case, tell us where the screen will live, what content you need to publish, and how quickly you want to deploy.</p>
`

const faqHtml = `
  <p>These questions address the most common topics buyers raise when selecting display hardware, mounting, deployment support, and rollout timing.</p>
`

export const localContent: CmsData = {
  settings: {
    brandName: 'Digital Signage Gear',
    tagline: 'Digital signage kits and hardware for fast, self-managed rollouts.',
    primaryEmail: 'hello@digitalsignagegear.com',
    footerNote: 'A large catalog of digital signage screens, mounts, accessories, and deployment-ready kits.',
    logoPath: '/wordpress-media/003-logo-transparent-385x131.png',
    logoLightPath: '/wordpress-media/004-white-logo-transparent-385x131.png',
  },
  homepage: {
    eyebrow: 'Large catalog of digital signage gear',
    title: 'Ready-to-install digital signage kits that reduce cost and speed up deployment.',
    summary:
      'Browse a broad catalog of screens, mounts, accessories, and industry-ready kits designed for straightforward installation and faster go-live timelines.',
    processTitle: 'A simpler way to source signage hardware.',
    ctaTitle: 'Tell us your industry and we’ll narrow the right kit.',
    ctaBody:
      'Use the quote-request flow for a curated recommendation, or move selected items into checkout to start a purchasing conversation.',
    heroMediaPath: '/wordpress-media/076-Kit-animation-1.gif',
    metrics: [
      { label: 'Catalog items online', value: String(totalCatalogProducts) },
      { label: 'Knowledge base articles', value: String(parsedWordpress.posts.length) },
      { label: 'Core buying journeys', value: '6' },
    ],
    industries: [
      {
        name: 'Airport',
        description: 'Wayfinding and passenger information kits designed for visibility and uptime.',
        imagePath: '/wordpress-media/064-Products-in-the-boxairport-385x240.jpg',
      },
      {
        name: 'Business',
        description: 'Lobby, meeting-room, and employee communication packages for office environments.',
        imagePath: '/wordpress-media/060-Products-in-the-boxBuisness-385x240.jpg',
      },
      {
        name: 'Healthcare',
        description: 'Waiting-room and patient communication deployments that prioritize clarity.',
        imagePath: '/wordpress-media/063-Products-in-the-boxHospital-385x240.jpg',
      },
      {
        name: 'Gaming',
        description: 'Promotional and venue signage kits for fast refresh cycles and bright visuals.',
        imagePath: '/wordpress-media/062-Products-in-the-boxgaming-385x240.jpg',
      },
      {
        name: 'Entertainment',
        description: 'Menu, event, and venue signage built to keep installation straightforward.',
        imagePath: '/wordpress-media/061-Products-in-the-boxentertainment-385x240.jpg',
      },
    ],
    steps: [
      {
        title: 'Choose the deployment context',
        body: 'Start with industry, screen size, and mount requirements instead of forcing customers through raw AV parts.',
      },
      {
        title: 'Build the right kit',
        body: 'Compare screens, mounts, accessories, and use-case fit so each deployment starts with the right hardware mix.',
      },
      {
        title: 'Move from quote to purchase',
        body: 'Request pricing, confirm fulfillment details, and keep the buying process moving without unnecessary back-and-forth.',
      },
    ],
  },
  pages: {
    about: {
      title: 'Welcome to Digital Signage Gear',
      summary:
        'We help businesses source practical digital signage hardware without the overhead of a traditional AV procurement cycle.',
      html: aboutHtml,
    },
    contact: {
      title: 'Contact form',
      summary: 'Use this form to tell us what you are deploying, where it will live, and what constraints matter most.',
      html: contactHtml,
    },
    faq: {
      title: 'Frequently asked questions',
      summary: 'Practical buyer questions about choosing, installing, and supporting signage kits.',
      html: faqHtml,
    },
  },
  faqs: [
    {
      id: 'what-is-a-kit',
      question: 'What is a digital signage kit?',
      answer: 'A kit packages the essential hardware and setup guidance needed to launch a display without sourcing each part separately.',
    },
    {
      id: 'what-is-included',
      question: 'What does the kit include?',
      answer: 'The exact bundle depends on the product, but typically includes the display hardware, mounting or accessory options, and clear setup instructions.',
    },
    {
      id: 'how-hard-is-assembly',
      question: 'How easy is it to assemble the kit?',
      answer: 'The site is positioned around self-installation. The goal is a rollout that feels closer to following good flat-pack instructions than hiring a full AV integration team.',
    },
    {
      id: 'tech-skills',
      question: 'Do I need technical skills to set up the kit?',
      answer: 'Basic installation comfort helps, but the model is designed for small and mid-sized businesses that need straightforward deployment rather than specialist installers.',
    },
    {
      id: 'content-customization',
      question: 'Can I customize the content displayed on the signage?',
      answer: 'Yes. The hardware is meant to support flexible content strategies, whether you are running menus, promotions, internal messaging, or wayfinding.',
    },
    {
      id: 'support',
      question: 'Is support available after purchase?',
      answer: 'That is part of the value proposition. The site messaging emphasizes documentation, videos, and follow-up guidance to keep installation friction low.',
    },
  ],
  posts: parsedWordpress.posts.map((post) => ({
    id: post.id,
    title: post.title,
    slug: post.slug,
    excerpt: post.excerpt || summarize(post.html, post.title),
    html: post.html,
    publishedAt: post.publishedAt,
    author: post.author,
    categories: post.categories ?? [],
    tags: post.tags ?? [],
  })),
  products: [...parsedWordpress.products.filter(shouldIncludeImportedProduct).map(normalizeProduct), ...curatedSupplementalProducts],
}